import type { TicketedEventSchoolBranding } from '@school-journey/shared-types'

function initials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean)
  if (words.length === 0) return '·'
  return words
    .slice(0, 2)
    .map(w => w[0]!.toUpperCase())
    .join('')
}

/**
 * The school crest for the public page chrome, in whichever variant suits the
 * viewer's theme.
 *
 * A school that uploaded BOTH variants gets both rendered, one hidden per mode
 * by CSS rather than picked in JS: these headers are server-rendered and the
 * theme class lands on `<html>` client-side, so a JS pick would either render
 * the wrong crest until hydration or force the whole header to be a client
 * component. A school with only one variant uses it in both modes — a
 * light-only crest on a dark header still beats no crest at all.
 *
 * Falls back to two-letter initials when there is no logo, painted with the
 * brand tokens so an un-logoed school still reads correctly in both modes.
 */
export function SchoolLogo({ school }: { school: TicketedEventSchoolBranding }) {
  const { logoUrlLight, logoUrlDark, name } = school

  if (logoUrlLight && logoUrlDark) {
    return (
      <>
        <img src={logoUrlLight} alt={name} className="h-10 w-auto dark:hidden" />
        <img src={logoUrlDark} alt={name} className="hidden h-10 w-auto dark:block" />
      </>
    )
  }

  const logo = logoUrlLight ?? logoUrlDark
  if (logo) return <img src={logo} alt={name} className="h-10 w-auto" />

  return (
    <div className="bg-primary text-primary-foreground grid h-[38px] w-[38px] place-items-center rounded-full text-base font-extrabold">
      {initials(name)}
    </div>
  )
}
